
/**
 *      Data-driven UI logic to display viewer page
 *
 *      @author Irina Patrikeeva <irina.i.ipatrikeeva@nasa.gov>
 *
 *      Created on 09/07/2017
 *      Modified on 03/04/2019
 */


/**
 * Create a JavaScript object based on the JSON View Model
 * @param model {Object} - view model to render
 * @param data {Object} - record data  object
 * @param jsonModel {object} - data JSON model
 * @returns {*} - single object if type is not multiple, and array of objects if type is multiple
 */
function parseRecordData( model, data, jsonModel ) {
    var dataObject = {};
    if ( model && data && jsonModel ) {
        // Graph object
        if ( model.graph ) {
            dataObject = getGraphDataObject( model );
        }
        else if ( model.default && model.default.toString() === "true" ) {
            // parses the data and displays the layout regions
            displayDefaultDataObject( model, jsonModel, data );
        }
        // Nested object or an array of objects
        else if ( ( model.nested && model.nested.toString() === "true") || ( model.multiple && model.multiple.toString() === "true" ) ) {
            dataObject = getComplexDataObject( model, jsonModel, data );
        } else {
            // One object with multiple fields
            dataObject = getSimpleDataObject( model, data );
        }
    }
    return dataObject;
}

/**
 * Construct a JavaScript object for the data needed in the view from the data object
 * @param model {object} - JSON view model of the region
 * @param data {object} - region data object
 * @returns {*} - JavaScript object or array of objects with field names as keys and values of {"value": <value> and "type": <type>}
 */
function getSimpleDataObject( model, data ) {
    var dataObject,
        additionalFields = model.additionalFields,
        k,
        field;

    dataObject = getDataObjectFields( model, data );

    if ( additionalFields ) {
        for ( k = 0; k < additionalFields.length; k++ ) {
            field = additionalFields[ k ];
            dataObject[ field.name ] = { "value": field.value, "type": typeof field.value };
        }
        dataObject = updateGlobalObject( dataObject, model );
    }

    return dataObject;
}

/**
 *
 * @param model {object} - JSON view model of the region
 * @param data {object} - region data object
 * @returns {object}
 */
function getDataObjectFields( model, data ) {
    var fields,
        fieldValues,
        fieldNames,
        dataName,
        key,
        value,
        originalValue,
        fieldValue,
        dataObject = {},
        i,
        j,
        k;

    if ( model ) {
        fields = model.fields;
        if ( fields ) {
            for ( j = 0; j < fields.length; j++ ) {
                fieldNames = fields[ j ].names;
                dataName = fields[ j ].data;
                for ( key in data ) {
                    if ( data.hasOwnProperty( key ) && key === dataName ) {

                        for ( k = 0; k < fieldNames.length; k++ ) {

                            value = getElasticSearchField( fieldNames[ k ], data[ key ] );
                            if ( fieldNames[ k ] === "_type" ) {
                                // Get type label from cached UI model
                                originalValue = value;
                                value = getDataTypeLabel( value );
                                if ( ! value && originalValue ) {
                                    value = originalValue.capitalizeFirstLetter();
                                }
                            }
                            if ( ! ( value === null || value === undefined ) ) {
                                dataObject[ fieldNames[ k ] ] = { "value": value, "type": typeof value };
                            }
                        }
                    }
                }
                fieldValues = fields[ j ].values;
                // If model fields have values array, extract those values and save them into the dataObject
                if ( fieldValues ) {
                    for ( i = 0; i < fieldValues.length; i++ ) {
                        fieldValue = fieldValues[ i ];
                        value = getDataFieldValue( fieldValue.field, fieldValue.value, data );
                        if ( value !== null ) {
                            dataObject[ fieldValue.name ] = { "value": value, "type": typeof value };
                        }
                    }
                }
            }
        }
    }

    return dataObject;
}

/**
 * Update data object with globally stored variables like page number, sort order, etc.
 * @param dataObject {object} - object to update
 * @param model {object} - region model
 * @returns {*}
 */
function updateGlobalObject( dataObject, model ) {

    var regionName,
        vars,
        key,
        i;

    // Check if additional fields are stored in the global store
    regionName = model.name;
    vars = getRegionVariables( regionName );
    if ( vars ) {

        // Update the data object if those fields were found
        for ( i = 0; i < vars.length; i++ ) {

            for ( key in dataObject ){
                if ( dataObject.hasOwnProperty( key ) ){
                    if ( vars[i].name === key ) {
                        dataObject[ key ].value = vars[ i ].value;
                    }
                }
            }
        }
    }
    return dataObject;
}

/**
 * Construct a Backbone model for the parent fields defined in the LayoutView JSON model
 * @param model {object} - LayoutView JSON model
 * @returns {BackboneModel}
 */
function createParentFieldsModel( model ) {
    var bbModel = new BackboneModel,
        parentFields = model.parentFields,
        field,
        fieldValue,
        valueArray,
        fields,
        sortOn,
        data,
        hits,
        dataName,
        recordJSON,
        properties,
        recordCollection,
        fieldNames,
        i,
        j;

    if ( model && GlobalPageState ) {

        fields = model.fields;

        // Set the fields of the parent template
        for ( i = 0; i < parentFields.length; i++ ) {
            field = parentFields[ i ];

            fieldValue = field.value;

            // If type value is undefined, use the current record type value
            if ( field && field.name === "type" && ! fieldValue ) {
                // Use current record type as the default
                fieldValue = GlobalPageState.type;
            }

            valueArray = [];

            for ( j = 0; j < fields.length; j++ ) {
                dataName = fields[ j ].data;

                if ( dataName ) {
                    // check if value is an array
                    // For array values, create value objects for each value
                    if ( field.name === "headers" && typeof fieldValue === "object" ) {
                        sortOn = model.sortOn;
                        recordJSON = getJSONDataModel( dataName );
                        if ( recordJSON ) {
                            // get a collection of fields from a UI model

                            if ( recordJSON.properties && recordJSON.properties.length === 1 ) {
                                properties = recordJSON.properties[ 0 ];

                                if ( properties && properties.properties ) {
                                    recordCollection = getBackboneCollection( properties.properties );

                                    if ( recordCollection ) {
                                        // Iterate through field names and get field labels to use as parent values
                                        fieldNames = fields[ j ].names;
                                        valueArray = getHeaderFields( model.name, recordCollection, fieldNames, sortOn, valueArray );
                                    }
                                }
                            }
                        }
                    }

                    if ( field.name === "total" ) {
                        if ( GlobalPageState && GlobalPageState.data ) {
                            data = GlobalPageState.data[ dataName ];
                            if ( data ) {
                                hits = getResponseHits( data );
                                if ( hits ) {
                                    fieldValue = hits.total;
                                }
                            }
                        }
                    }

                    // Get the type value from the API model "name" value that matches the fields.data value in the View model
                    if ( field.name === "type" && fieldValue === "data" ) {
                        // Get the region data model name
                        fieldValue = getAPIModelDataType( GlobalPageState.type, dataName );
                    }
                }
            }

            if ( typeof fieldValue === "object" ) {
                fieldValue = valueArray;
            }

            bbModel.set( field.name, fieldValue );
        }
    }

    return bbModel;
}

/**
 * Get data field value
 * @param fieldName {string} - name of the field (key)
 * @param value {string} - name of the value to get
 * @param data {object} - record data object
 * @returns {*} non-empty value if found
 */
function getDataFieldValue(fieldName, value, data) {

    var result = null,
        key,
        field,
        response,
        j;

    if (fieldName && value && data) {
        for (key in data) {
            if (data.hasOwnProperty(key)) {
                response = getResponseObject(data[key]);
                if (response && response.length > 0) {
                    for (j = 0; j < response.length; ++j) {
                        var sourceObject = getDataField(response[j], "_source");
                        field = getDataField(sourceObject, fieldName);
                        if (field) {
                            return field[value];
                        }
                    }
                }
            }
        }
    }

    return result;
}

/**
 * Get first-level field from an object of key-value pairs
 * @param data {object} - JavaScript object
 * @param field {string} - key value
 * @returns {*} - if found returns the value of the field in the object
 */
function getDataField(data, field) {
    var result = null,
        key;

    for (key in data) {
        if (data.hasOwnProperty(key)) {
            if (key === field) {
                return data[key];
            }
        }
    }
    return result;
}

/**
 * Get an object representation for nested view and view with multiple objects
 * @param model {Object} - view model to render
 * @param jsonModel {object} - data JSON model
 * @param data {Object} - record data object
 * @returns {*} single object if type is nested, and array of objects if type is multiple
 */
function getComplexDataObject( model, jsonModel, data ) {
    var dataObject,
        response,
        key,
        j,
        objectArray = [],
        extraData = getExtraData( model, data );

    // Nested region view or a view of multiple objects
    if ( hasKeys( data ) ) {
        for ( key in data ) {
            if ( data.hasOwnProperty( key ) ) {
                // extraData object is the data that is not needed in the view but is included in the data object
                if ( ! ( extraData && extraData.hasOwnProperty( key ) ) ) {
                    response = getResponseObject( data[ key ] );
                    if ( response && response.length > 0 ) {
                        for ( j = 0; j < response.length; j++ ) {
                            dataObject = parseDataSource( response[ j ], model, jsonModel );
                            if ( model.multiple && hasKeys( dataObject )) {
                                objectArray.push( dataObject );
                            }
                        }
                    }
                }
            }
        }
    }

    if ( model.multiple ) {
        dataObject = objectArray; // Data object will be an array of objects
    }

    return dataObject;
}

/**
 * Construct an object of dependent data that's not needed for the view
 * @param model {Object} - view model to render
 * @param data {Object} - record data  object
 * @returns {object} - JavaScript object of data needs not to be displayed
 */
function getExtraData( model, data ) {
    var i,
        k,
        key,
        extraData = {};

    if ( model.dependsOn ) {
        for ( i = 0; i < model.dependsOn.length; i++ ) {
            for ( key in data ) {
                if ( data.hasOwnProperty( key ) ) {
                    for ( k = 0; k < model.fields.length; k++ ) {
                        if ( key === model.dependsOn[ i ] && model.fields[ k ].data !== key ) {
                            extraData[key] = true;
                        }
                    }
                }
            }
        }
    }
    return extraData;
}

/**
 * Get a Backbone collection from a nested object inside a Backbone collection
 * @param collection {object} - Backbone collection containing a nested object in one of its models
 * @param nestedName {string} - name of the nested object
 * @returns {*}
 */
function getNestedDataModel(collection, nestedName) {
    var result = null,
        nestedModel,
        modelJSON;
    if (collection && nestedName) {
        nestedModel = lookupFieldModel(collection, nestedName);
        if (nestedModel) {
            modelJSON = nestedModel.toJSON();
            if (modelJSON) {
                result = getBackboneCollection(modelJSON.properties);
            }
        }
    }
    return result;

}

/**
 * Extract nested field data
 * @param dataName {string} - name of the data
 * @param fieldNames {[string]} - array of nested field names
 * @returns {object} - JavaScript object that has the data name as the key and array of objects as values
 */
function getNestedFieldData(dataName, fieldNames) {
    var j,
        fieldName,
        compoundName,
        data,
        fieldData,
        dataObject = {};

    for (j = 0; j < fieldNames.length; j++) {
        fieldName = fieldNames[j];
        if (fieldName.indexOf(".") > -1) {
            compoundName = fieldName.split(".");
            if (compoundName && compoundName.length === 2) {
                fieldName = compoundName[0];
            }
        }
        if (dataName && GlobalPageState && GlobalPageState.data) {
            data = GlobalPageState.data[dataName]; // retrieve data if global state has all of the needed data
            if (data) {
                fieldData = getElasticSearchField(fieldName, data);
                if (!dataObject || !dataObject[dataName]) {
                    dataObject[dataName] = fieldData;
                }
            }
        }
    }
    return dataObject;
}

/**
 * Return the number of objects to show. Some arrays of objects may be larger than the desired number of objects to display.
 * @param nObjects {number} - total number of objects
 * @param additionalFields {object} - JavaScript object that contains information about the starting and ending index of the table items
 * @returns {*} - If the number of items in the array is smaller than the table, return that number. If it is larger, get the number of items to show based on the view model info in additionalFields.
 */
function getNumberOfObjectsToShow(nObjects, additionalFields) {

    var start,
        end,
        result = nObjects,
        nItems,
        i;

    for (i = 0; i < additionalFields.length; i++) {
        if (additionalFields[i].name === "start") {
            start = additionalFields[i].value;
        }
        if (additionalFields[i].name === "end") {
            end = additionalFields[i].value;
        }
    }
    if (start && end) {
        nItems = end - start + 1;
    }

    if (nItems && nItems >= 0 && nItems < nObjects) {
        result = nItems;
    }

    return result;
}


/**
 * Parse data _source object based on the region view model fields and parentFields
 * @param response {object} - ES response object
 * @param layoutModel {object} - region view model
 * @param jsonModel {object} - data JSON model
 * @returns {*} - an object or an array of objects that contain key-value pairs, where field names are keys, and labels, values and types are values
 */
function parseDataSource( response, layoutModel, jsonModel ) {

    var dataObject,
        fields = layoutModel.fields,
        dataCollection,
        dataName,
        dataType,
        fieldNames,
        properties,
        i;

    if ( layoutModel ) {

        for ( i = 0; i < fields.length; i++ ) {
            dataName = fields[ i ].data;
            fieldNames = fields[ i ].names;

            dataType = getDataModelName( dataName ); // get the name of the data type

            if ( fields[i].multiple ) {
                // data type will be an array of types
                // find the data model by using response data type
                if ( response ) {
                    dataType = response._type;
                }
            }

            // Check if the right JSON model is passed in
            if ( jsonModel && jsonModel.properties && jsonModel.properties.length === 1 ) {
                properties = jsonModel.properties[ 0 ];
                if ( properties ) {
                    var modelType = properties.name;
                    if ( modelType && dataType && modelType !== dataType ) {
                        // Get the right data model to match the data type
                        jsonModel = getDataModel( dataType, false );
                    }
                }
            }

            if ( ! jsonModel ) {
                jsonModel = getDataModel( dataType, false ); // fall back on the global data model
            }

            // Convert JavaScript data model into a Backbone.Collection object
            dataCollection = convertDataModelPropertiesToCollection( jsonModel );
            if ( dataCollection ) {
                dataObject = parseDataCollection( dataCollection, layoutModel, response, fieldNames );
            }
        }
    }
    return dataObject;
}

/**
 * Add a key-value pair to the dataObject object for non-nested non-multiple templates
 * @param dataObject {object} - JavaScript object of the view data
 * @param dataObjectField {string} - record data attribute
 * @param fieldModel {Backbone.Model} - field Backbone model
 * @param key {string} - field name needed for the view
 * @returns {*}
 */
function parseSimpleObject(dataObject, dataObjectField, fieldModel, key) {

    var label = fieldModel.get("label") || key,
        value = dataObjectField,
        type = fieldModel.get("type") || typeof dataObjectField;

    if (key === "_type") { // TODO: use global store to get the labels
        label = "TBD"; //DataTypes[dataObjectField];
        value = label;
    }
    dataObject[key] = { "label": label, "value": value, "type": type };

    return dataObject;
}

/**
 * Creates an array of objects for nested templates with multiple objects
 * @param fieldValues {[string]} - name of the nested field needed for the view
 * @param dataObjectField {[object]} - array of object inside the nested field
 * @param collection {Backbone.Collection} - Backbone collection of the nested objects built from record JSON model
 * @param additionalFields {[object]} - array of objects needed for display that is not part of the data
 * @returns {*}
 */
function parseArrayOfObjects(fieldValues, dataObjectField, collection, additionalFields) {
    var dataObjects = [],
        dataObject,
        j,
        l,
        item,
        itemValue,
        propFieldModel,
        label,
        nObjects,
        nObjectsToShow,
        compoundFieldName,
        fieldName;

    if (fieldValues && fieldValues.length > 0) {
        if (fieldValues[0].indexOf(".") > -1) {
            var compoundName = fieldValues[0].split(".");
            if (compoundName && compoundName.length === 2) {
                if (dataObjectField) {
                    nObjects = dataObjectField.length;
                    if (nObjects > 0) {
                        nObjectsToShow = getNumberOfObjectsToShow(nObjects, additionalFields);
                    }
                }
                // Get nested collection of data models
                collection = getNestedDataModel(collection, compoundName[0]);
            }
        }
    }

    if (dataObjectField && nObjectsToShow) {
        for (j = 0; j < nObjectsToShow; j++) {
            item = dataObjectField[j];
            // Get the fields needed by the view from each item
            if (fieldValues) {
                dataObject = {};
                for (l = 0; l < fieldValues.length; l++) {
                    if (fieldValues[l].indexOf(".") > -1) {
                        compoundFieldName = fieldValues[l].split(".");
                        if (compoundFieldName && compoundFieldName.length === 2) {
                            fieldName = compoundFieldName[1];
                            // find the values in each item in the array
                            itemValue = item[fieldName];
                            propFieldModel = lookupFieldModel(collection, fieldName);
                            if ( propFieldModel ) {
                                label = propFieldModel.get("label") || fieldName;
                                if (label && itemValue) {
                                    dataObject[fieldValues[l]] = { "label": label, "value": itemValue, "type": propFieldModel.get("type") || typeof itemValue };
                                }
                            }

                        }
                    }
                }
                dataObjects.push(dataObject);
            }
        }
    }

    return dataObjects;
}

/**
 * Creates a object for nested templates with one object
 * @param dataObjectField {[object]} - array of object inside the nested field
 * @param collection {Backbone.Collection} - Backbone collection of the nested objects built from record JSON model
 * @returns {*}
 */
function parseNestedObject(dataObjectField, collection) {
    var dataObject = {},
        nestedKey,
        propFieldModel,
        label;
    for (nestedKey in dataObjectField) {
        if (dataObjectField.hasOwnProperty(nestedKey)) {
            propFieldModel = lookupFieldModel(collection, nestedKey);
            if (propFieldModel && nestedKey === propFieldModel.get("name") && dataObjectField[nestedKey] !== null) {
                label = propFieldModel.get("label") || nestedKey;
                dataObject[nestedKey] = { "label": label, "value": dataObjectField[nestedKey], "type": propFieldModel.get("type") || typeof sourceObject[nestedKey] };
            }
        }
    }
    return dataObject;
}

/**
 * Check if object has keys
 * @param object {object} - JavaScript object
 * @returns {boolean} - true if object has keys, false if undefined, null or empty
 */
function hasKeys(object) {
    var result = false;
    if (  object && Object.keys(object).length > 0 ) {
        result = true;
    }
    return result;
}

/**
 * Wrap the function that change icons on an accordion click
 * @param event {object} - click event
 */
function wrapperAccordionIconClick( event ) {
    _.debounce( changeAccordionIcon( event ), 300 );

}

/**
 * Change icons in the accordion title on click event
 * @param event {object} - click event
 */
function changeAccordionIcon( event ) {
    var button,
        buttonIcon,
        buttonTitle,
        iconClass,
        hiddenTitle = "Expand", // title of the icon when accordion is collapsed
        visibleTitle = "Collapse"; // title of the icon when accordion is expanded

    if ( event && event.currentTarget ) {
        button = $( event.currentTarget );

        if ( button ) {

            buttonIcon = $( button ).find( "i" );

            if ( buttonIcon ) {
                buttonTitle = $( buttonIcon ).attr( "title" );

                // change the button when accordion is hidden
                if ( buttonTitle ===  hiddenTitle ) {

                    // Change button title
                    if ( buttonIcon.length === 1 ) {
                        $( buttonIcon )[0].setAttribute( "title", visibleTitle );

                        // Change the icon to right caret

                        iconClass = $( buttonIcon ).attr( "class" );

                        if ( iconClass && iconClass.includes( "fa-plus" ) ) {
                            $( buttonIcon )[0].setAttribute( "class", "fa fa-minus" );
                        }
                    }

                }
                else if ( buttonTitle === visibleTitle ) {

                    // change the button when accordion is visible

                    // Change button title
                    if ( buttonIcon.length === 1 ) {
                        $( buttonIcon )[0].setAttribute( "title", hiddenTitle );

                        // Change the icon to right caret

                        iconClass = $( buttonIcon ).attr( "class" );

                        if ( iconClass && iconClass.includes( "fa-minus" ) ) {
                            $( buttonIcon )[0].setAttribute( "class", "fa fa-plus" );
                        }
                    }
                }
            }
        }
    }
}

/**
 * Route user to the single viewer page.
 * @param id {string} Record id
 * @param viewerType {string} Viewer type.
 */
function clickViewerLink( id, viewerType ) {

    var route,
        alias;

    if ( id && viewerType ) {

        route = "view/";

        alias = App.globalState.alias;

        if ( alias ) {
            route += alias + "/";
        }
        route += viewerType + "/" + id;

        // Per JIRA USRA-604, set global graph collapse flag to false
        App.globalState.collapse = false;

        router.navigate( route, { trigger: true } );
    }
}

/**
 * Expand the first metadata region (accordion)
 */
function expandFirstAccordion() {
    var accordions,
        metadata;

    // expand the first metadata accordion
    accordions = $('.accordion-button');
    if ( accordions ) {
        metadata = accordions[0];
        if ( metadata ) {
            metadata.click();
        }
    }
}